import { CheckCircle2, TrendingUp, Users, Cpu, Globe2, IndianRupee, Target } from "lucide-react";
import { Reveal, Eyebrow } from "../components/Reveal";
import { LeadForm } from "../components/LeadForm";
import { VerifyGate } from "../components/VerifyGate";

const WHY_ICONS = [Users, Target, IndianRupee, Cpu, TrendingUp, Globe2];
const FUND_COLORS = ["bg-violet-500", "bg-blue-500", "bg-slate-500", "bg-amber-500", "bg-teal-500"];

const InvestorsContent = ({ d }) => (
    <div className="relative overflow-hidden" data-testid="investors-page">
        <div className="glow-orb left-[25%] top-[-5%] h-[400px] w-[560px] bg-amber-500/10" />
        <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
            <Reveal className="text-center">
                <Eyebrow>For Investors</Eyebrow>
                <h1 className="mt-4 font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl" data-testid="investors-heading">
                    Invest in efficiency. <span className="amber-gradient-text">Build a bigger tomorrow.</span>
                </h1>
                <p className="mx-auto mt-4 max-w-2xl text-slate-400">{d.intro}</p>
            </Reveal>

            <div className="mt-12 grid gap-4 sm:grid-cols-3" data-testid="investor-highlights">
                {d.highlights.map((h, i) => (
                    <Reveal key={h.label} delay={i * 0.08}>
                        <div className="glow-card rounded-2xl p-5 text-center">
                            <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-slate-500">{h.label}</p>
                            <p className="mt-2 font-display text-lg font-extrabold text-white">{h.value}</p>
                        </div>
                    </Reveal>
                ))}
            </div>

            <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {d.why.map((w, i) => {
                    const Icon = WHY_ICONS[i % WHY_ICONS.length];
                    return (
                        <Reveal key={w.title} delay={(i % 3) * 0.1}>
                            <div className="glow-card h-full rounded-2xl p-6" data-testid={`investor-why-${i}`}>
                                <div className="mb-4 inline-flex rounded-xl bg-amber-500/10 p-3 text-amber-400">
                                    <Icon className="h-5 w-5" />
                                </div>
                                <h3 className="font-display text-base font-bold text-white">{w.title}</h3>
                                <p className="mt-2 text-sm leading-relaxed text-slate-400">{w.desc}</p>
                            </div>
                        </Reveal>
                    );
                })}
            </div>

            <div className="mt-16 grid gap-6 lg:grid-cols-2">
                <Reveal>
                    <div className="glow-card h-full rounded-2xl p-8" data-testid="investor-funds">
                        <h2 className="font-display text-xl font-bold text-white">Use of Funds — ₹5 Crore</h2>
                        <p className="mt-1 text-sm text-slate-500">Strategic investment for a stronger tomorrow.</p>
                        <div className="mt-7 space-y-5">
                            {d.use_of_funds.map((u, i) => (
                                <div key={u.label}>
                                    <div className="mb-1.5 flex items-center justify-between text-sm">
                                        <span className="text-slate-300">{u.label}</span>
                                        <span className="font-semibold text-slate-200">{u.pct}% · {u.amount}</span>
                                    </div>
                                    <div className="h-2.5 overflow-hidden rounded-full bg-white/5">
                                        <div className={`h-full rounded-full ${FUND_COLORS[i % FUND_COLORS.length]}`} style={{ width: `${u.pct}%` }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </Reveal>
                <Reveal delay={0.15}>
                    <div className="glow-card h-full rounded-2xl p-8" data-testid="investor-validation">
                        <h2 className="font-display text-xl font-bold text-white">Current Validation</h2>
                        <p className="mt-1 text-sm text-slate-500">Building today for a bigger tomorrow.</p>
                        <ul className="mt-7 space-y-4">
                            {d.validation.map((v) => (
                                <li key={v} className="flex items-center gap-3 text-sm text-slate-300">
                                    <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-400" /> {v}
                                </li>
                            ))}
                        </ul>
                        <div className="mt-8 rounded-xl border border-amber-500/25 bg-amber-500/5 p-4 text-xs leading-relaxed text-amber-200/80">
                            {d.disclaimer}
                        </div>
                    </div>
                </Reveal>
            </div>

            <Reveal className="mt-16">
                <div className="mx-auto max-w-3xl">
                    <div className="glow-card rounded-3xl p-7 sm:p-10" data-testid="investor-form-card">
                        <h2 className="text-center font-display text-2xl font-bold text-white">
                            Start an <span className="amber-gradient-text">investor conversation</span>
                        </h2>
                        <p className="mx-auto mt-2 max-w-md text-center text-sm text-slate-500">
                            Share your details and our founding team will reach out directly. Confidential and no-obligation.
                        </p>
                        <div className="mt-8">
                            <LeadForm type="investor" testid="investor" />
                        </div>
                    </div>
                </div>
            </Reveal>
        </section>
    </div>
);

export default function Investors() {
    return (
        <VerifyGate
            page="investors"
            title="Investor Information"
            teaser="Seed round details, use of funds and company validation — available after a quick email verification."
        >
            {(data) => <InvestorsContent d={data} />}
        </VerifyGate>
    );
}
